// Association portal: error mapping for the association writes owned by the
// profile page (data form, publication toggle, delete).
//
// The backend answers association failures with a STRUCTURED body:
// `400 {errorCode, message}` from GlobalExceptionHandler with the codes in
// AssociationErrorCode (never 401/403). A bean-validation failure is a flat
// `400 {field: message}` map. A write on an association that is already
// soft-deleted passes the pairing check and then 404s (requireAssociation),
// with a plain-text body.

/** The shape this codebase's axios rejections actually have. */
type RejectionLike = {
  response?: {
    status?: number;
    data?: unknown;
  };
};

type StructuredBody = { errorCode?: unknown; message?: unknown } & Record<
  string,
  unknown
>;

const rejection = (error: unknown): RejectionLike =>
  typeof error === "object" && error !== null ? (error as RejectionLike) : {};

const status = (error: unknown): number | undefined =>
  rejection(error).response?.status;

/** The body only when it is a JSON object; a plain-text body yields nothing. */
const body = (error: unknown): StructuredBody | undefined => {
  const data = rejection(error).response?.data;
  return typeof data === "object" && data !== null && !Array.isArray(data)
    ? (data as StructuredBody)
    : undefined;
};

const errorCode = (error: unknown): string | undefined => {
  const code = body(error)?.errorCode;
  return typeof code === "string" && code ? code : undefined;
};

const backendMessage = (error: unknown): string | undefined => {
  const message = body(error)?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
};

/**
 * A bean-validation body is a flat `{field: message}` map with no errorCode.
 * The first string value is the most useful thing to show.
 */
const firstValidationMessage = (error: unknown): string | undefined => {
  const data = body(error);
  if (!data || data.errorCode != null) {
    return undefined;
  }
  const first = Object.values(data).find(
    (value) => typeof value === "string" && value.trim(),
  );
  return typeof first === "string" ? first : undefined;
};

export const ASSOCIATION_INVALID_INPUT_ERROR =
  "Podaci nisu prihvaćeni. Proverite unos i pokušajte ponovo.";
export const ASSOCIATION_NOT_ADMINISTERED_ERROR =
  "Nemate pravo da upravljate ovim udruženjem.";
export const ASSOCIATION_SAVE_ERROR = "Čuvanje nije uspelo. Pokušajte ponovo.";
export const ASSOCIATION_DELETE_ERROR =
  "Brisanje nije uspelo. Pokušajte ponovo.";

/**
 * Backend `AssociationErrorCode.ASSOCIATION_NOT_ADMINISTERED`, always a 400:
 * the account no longer administers this association. The caller re-reads
 * `/association/mine`, which drops the row and moves the selection.
 */
export const isAssociationNotAdministered = (error: unknown): boolean =>
  status(error) === 400 && errorCode(error) === "ASSOCIATION_NOT_ADMINISTERED";

/**
 * True when the caller must re-read `/association/mine`: the pairing is gone
 * (NOT_ADMINISTERED) or the association itself is gone (404 after a delete
 * elsewhere). Both are answered by the server's list, not by guessing.
 */
export const shouldRefetchAssociationsAfterError = (error: unknown): boolean =>
  isAssociationNotAdministered(error) || status(error) === 404;

const mapAssociationError = (error: unknown, fallback: string): string => {
  if (status(error) !== 400) {
    return fallback;
  }
  const code = errorCode(error);
  if (code === "ASSOCIATION_INVALID_INPUT") {
    return backendMessage(error) ?? ASSOCIATION_INVALID_INPUT_ERROR;
  }
  if (code === "ASSOCIATION_NOT_ADMINISTERED") {
    return ASSOCIATION_NOT_ADMINISTERED_ERROR;
  }
  if (code) {
    // Any other code (e.g. a storage failure) says nothing about the input.
    return fallback;
  }
  return firstValidationMessage(error) ?? fallback;
};

/** `PUT /association/{id}` failures: the data form and the publication toggle. */
export const mapAssociationSaveError = (error: unknown): string =>
  mapAssociationError(error, ASSOCIATION_SAVE_ERROR);

/** `DELETE /association/{id}` failures: same codes, delete wording. */
export const mapAssociationDeleteError = (error: unknown): string =>
  mapAssociationError(error, ASSOCIATION_DELETE_ERROR);
